import React from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import { CloseOutlined, InboxOutlined, LinkOutlined, UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import { fetchSqlDataWithQuery, insertData } from '../../services/api';
import './XlsxSectionMapper.css';

const { Text } = Typography;
const { Dragger } = Upload;

const TABLE_NAME_RE = /^[a-zA-Z0-9_]+$/;
const EXCLUDED_COLUMNS = new Set(['id', 'created_at', 'updated_at', 'created_by', 'updated_by']);
const MAX_VISIBLE_ROWS = 250;
const MAX_VISIBLE_COLS = 50;
const MAX_LOADED_ROWS = 50000;
const PREVIEW_ROW_LIMIT = 8;

const NUMERIC_TYPES = new Set([
  'integer',
  'bigint',
  'smallint',
  'numeric',
  'real',
  'double precision',
  'decimal',
]);

export type TableColumnMeta = {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default?: string | null;
  ordinal_position: number;
  required: boolean;
  foreignKeys: Array<{ table: string; column: string }>;
};

type CellPos = { row: number; col: number };

type FieldMapping = {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
  address: string;
};

export type XlsxSectionMapperProps = {
  tableName?: string;
  onSuccess?: (result: any) => void;
};

const asRows = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const colToLetter = (index: number): string => {
  let s = '';
  let x = index + 1;
  while (x > 0) {
    const m = (x - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
};

const cellAddress = (row: number, col: number): string => `${colToLetter(col)}${row + 1}`;

const rangeAddress = (r1: number, c1: number, r2: number, c2: number): string => {
  const a = cellAddress(r1, c1);
  const b = cellAddress(r2, c2);
  return a === b ? a : `${a}:${b}`;
};

const normalizeRange = (a: CellPos, b: CellPos) => ({
  r1: Math.min(a.row, b.row),
  c1: Math.min(a.col, b.col),
  r2: Math.max(a.row, b.row),
  c2: Math.max(a.col, b.col),
});

const cellToString = (v: any): string => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return dayjs(v).isValid() ? dayjs(v).format('YYYY-MM-DD') : String(v);
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return String(v);
};

const isNumericType = (dataType: string): boolean => {
  const t = String(dataType || '').toLowerCase();
  return NUMERIC_TYPES.has(t) || t.startsWith('numeric') || t.startsWith('decimal');
};

const parseNumber = (raw: string): number | null => {
  const n = Number(String(raw).replace(',', '.').trim());
  return Number.isFinite(n) ? n : null;
};

const coerceValue = (raw: string, dataType: string): any => {
  if (raw === null || raw === undefined || raw === '') return null;
  const t = String(dataType || '').toLowerCase();
  if (isNumericType(t)) {
    const n = parseNumber(raw);
    return n === null ? raw : n;
  }
  if (t === 'boolean') {
    const s = String(raw).trim().toLowerCase();
    if (['true', 't', '1', 'yes', 'y'].includes(s)) return true;
    if (['false', 'f', '0', 'no', 'n'].includes(s)) return false;
    return raw;
  }
  if (t.includes('timestamp') || t === 'date') {
    const d = dayjs(raw);
    if (d.isValid()) return t === 'date' ? d.format('YYYY-MM-DD') : d.toISOString();
  }
  return raw;
};

const hasValue = (v: any): boolean => v !== null && v !== undefined && v !== '';

/** One value per insert row: a column (top→bottom), a row (left→right), or first column of a block. */
const extractRangeValues = (grid: string[][], mapping: FieldMapping): string[] => {
  const { r1, c1, r2, c2 } = mapping;
  const height = r2 - r1 + 1;
  const width = c2 - c1 + 1;
  const values: string[] = [];
  if (width === 1) {
    for (let r = r1; r <= r2; r += 1) values.push(grid[r]?.[c1] ?? '');
    return values;
  }
  if (height === 1) {
    for (let c = c1; c <= c2; c += 1) values.push(grid[r1]?.[c] ?? '');
    return values;
  }
  for (let r = r1; r <= r2; r += 1) values.push(grid[r]?.[c1] ?? '');
  return values;
};

const XlsxSectionMapper: React.FC<XlsxSectionMapperProps> = ({
  tableName: lockedTableName,
  onSuccess,
}) => {
  const isTableLocked = Boolean(lockedTableName);

  const [tableOptions, setTableOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [loadingTables, setLoadingTables] = React.useState(false);
  const [selectedTable, setSelectedTable] = React.useState<string>(lockedTableName || '');

  const [columns, setColumns] = React.useState<TableColumnMeta[]>([]);
  const [loadingColumns, setLoadingColumns] = React.useState(false);

  const [fileName, setFileName] = React.useState<string>('');
  const [sheetNames, setSheetNames] = React.useState<string[]>([]);
  const [sheetName, setSheetName] = React.useState<string | undefined>(undefined);
  const workbookRef = React.useRef<XLSX.WorkBook | null>(null);
  const [fullGrid, setFullGrid] = React.useState<string[][]>([]);
  const [grid, setGrid] = React.useState<string[][]>([]);
  const [truncated, setTruncated] = React.useState(false);
  const [showAllRows, setShowAllRows] = React.useState(false);

  const [armedField, setArmedField] = React.useState<string | null>(null);
  const [mappings, setMappings] = React.useState<Record<string, FieldMapping>>({});
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const dragRef = React.useRef<{ start: CellPos; current: CellPos } | null>(null);
  const [dragPreview, setDragPreview] = React.useState<{ start: CellPos; current: CellPos } | null>(null);
  const armedFieldRef = React.useRef<string | null>(null);
  const anchorRef = React.useRef<CellPos | null>(null);
  armedFieldRef.current = armedField;
  React.useEffect(() => {
    anchorRef.current = null;
  }, [armedField]);

  React.useEffect(() => {
    if (lockedTableName) setSelectedTable(lockedTableName);
  }, [lockedTableName]);

  React.useEffect(() => {
    if (isTableLocked) return;
    let cancelled = false;
    const run = async () => {
      setLoadingTables(true);
      try {
        const sql = `
          SELECT tablename AS table_name
          FROM pg_tables
          WHERE schemaname = 'public'
          ORDER BY tablename`;
        const res = await fetchSqlDataWithQuery(sql);
        if (cancelled) return;
        const rows = asRows(res);
        setTableOptions(
          rows
            .map((r: any) => String(r?.table_name || '').trim())
            .filter((name) => TABLE_NAME_RE.test(name))
            .map((name) => ({ label: name, value: name }))
        );
      } catch (e: any) {
        message.error(e?.message || 'Failed to load tables');
        setTableOptions([]);
      } finally {
        if (!cancelled) setLoadingTables(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isTableLocked]);

  React.useEffect(() => {
    const table = String(selectedTable || '').trim();
    setColumns([]);
    setArmedField(null);
    setMappings({});
    setOverrides({});
    if (!table) return;
    if (!TABLE_NAME_RE.test(table)) {
      message.error('Invalid table name');
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoadingColumns(true);
      try {
        const colsSql = `
          SELECT column_name, data_type, is_nullable, is_generated, column_default, ordinal_position
          FROM information_schema.columns
          WHERE table_schema = 'public'
            AND table_name = '${table}'
          ORDER BY ordinal_position`;
        const fkSql = `
          SELECT
            att.attname AS column_name,
            rel_ref.relname AS fk_table,
            att_ref.attname AS fk_column
          FROM pg_constraint con
          JOIN pg_class rel ON rel.oid = con.conrelid
          JOIN pg_namespace nsp ON nsp.oid = rel.relnamespace
          JOIN LATERAL unnest(con.conkey) WITH ORDINALITY AS cols(attnum, ord) ON TRUE
          JOIN pg_attribute att ON att.attrelid = rel.oid AND att.attnum = cols.attnum
          JOIN LATERAL unnest(con.confkey) WITH ORDINALITY AS fcols(attnum, ord) ON fcols.ord = cols.ord
          JOIN pg_class rel_ref ON rel_ref.oid = con.confrelid
          JOIN pg_attribute att_ref ON att_ref.attrelid = rel_ref.oid AND att_ref.attnum = fcols.attnum
          WHERE con.contype = 'f'
            AND nsp.nspname = 'public'
            AND rel.relname = '${table}'`;
        const [colsRes, fkRes] = await Promise.all([
          fetchSqlDataWithQuery(colsSql),
          fetchSqlDataWithQuery(fkSql),
        ]);
        if (cancelled) return;
        const fkByColumn = new Map<string, Array<{ table: string; column: string }>>();
        asRows(fkRes).forEach((r: any) => {
          const column_name = String(r?.column_name || '');
          const fk_table = String(r?.fk_table || '');
          const fk_column = String(r?.fk_column || '');
          if (!column_name || !fk_table || !fk_column) return;
          const list = fkByColumn.get(column_name) || [];
          if (!list.some((x) => x.table === fk_table && x.column === fk_column)) {
            list.push({ table: fk_table, column: fk_column });
          }
          fkByColumn.set(column_name, list);
        });
        const rows = asRows(colsRes);
        const next = rows
          .map((r: any) => {
            const column_name = String(r?.column_name || '');
            const column_default = r?.column_default ?? null;
            const is_nullable = String(r?.is_nullable || 'YES');
            const hasDefault = column_default !== null && String(column_default).trim() !== '';
            const isSerial = String(column_default || '').toLowerCase().includes('nextval(');
            const isGenerated = String(r?.is_generated || 'NEVER') !== 'NEVER';
            return {
              column_name,
              data_type: String(r?.data_type || ''),
              is_nullable,
              column_default,
              ordinal_position: Number(r?.ordinal_position || 0),
              required: is_nullable === 'NO' && !hasDefault,
              foreignKeys: fkByColumn.get(column_name) || [],
              isGenerated,
              isSerial,
            };
          })
          .filter((c) => c.column_name)
          .filter((c) => !EXCLUDED_COLUMNS.has(c.column_name))
          .filter((c) => !c.isSerial)
          .filter((c) => !c.isGenerated)
          .map(({ isSerial, ...rest }) => rest);
        setColumns(next);
        if (!next.length) message.warning('No insertable columns found for this table');
      } catch (e: any) {
        message.error(e?.message || 'Failed to load columns');
        setColumns([]);
      } finally {
        if (!cancelled) setLoadingColumns(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedTable]);

  const applySheet = React.useCallback((wb: XLSX.WorkBook, name: string) => {
    const ws = wb.Sheets[name];
    if (!ws) {
      setFullGrid([]);
      setGrid([]);
      setTruncated(false);
      return;
    }
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true }) as any[][];
    const rows = (raw || []).map((row) => (Array.isArray(row) ? row.map(cellToString) : []));
    const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 0);
    const rowsCapped = rows.slice(0, MAX_LOADED_ROWS);
    const clippedAll = rowsCapped.map((r) => {
      const padded = [...r];
      while (padded.length < Math.min(maxCols, MAX_VISIBLE_COLS)) padded.push('');
      return padded.slice(0, MAX_VISIBLE_COLS);
    });
    setFullGrid(clippedAll);
    setGrid(clippedAll.slice(0, MAX_VISIBLE_ROWS));
    setTruncated(rows.length > MAX_VISIBLE_ROWS || maxCols > MAX_VISIBLE_COLS || rows.length > MAX_LOADED_ROWS);
  }, []);

  React.useEffect(() => {
    if (!fullGrid.length) {
      setGrid([]);
      return;
    }
    setGrid(showAllRows ? fullGrid : fullGrid.slice(0, MAX_VISIBLE_ROWS));
  }, [fullGrid, showAllRows]);

  const loadWorkbook = React.useCallback(
    async (file: File) => {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!['csv', 'xls', 'xlsx'].includes(ext)) {
        message.error('Only CSV or Excel files are allowed');
        return false;
      }
      try {
        let wb: XLSX.WorkBook;
        if (ext === 'csv') {
          const text = await file.text();
          wb = XLSX.read(text, { type: 'string', cellDates: true });
        } else {
          const buf = await file.arrayBuffer();
          wb = XLSX.read(buf, { type: 'array', cellDates: true });
        }
        const names = wb.SheetNames || [];
        if (!names.length) {
          message.error('Workbook has no sheets');
          return false;
        }
        workbookRef.current = wb;
        setFileName(file.name);
        setSheetNames(names);
        setSheetName(names[0]);
        setMappings({});
        setOverrides({});
        setArmedField(null);
        applySheet(wb, names[0]);
        return false;
      } catch (e: any) {
        message.error(e?.message || 'Failed to read file');
        return false;
      }
    },
    [applySheet]
  );

  React.useEffect(() => {
    if (!sheetName || !workbookRef.current) return;
    applySheet(workbookRef.current, sheetName);
    setMappings({});
    setOverrides({});
    setArmedField(null);
  }, [applySheet, sheetName]);

  const commitRange = React.useCallback((start: CellPos, end: CellPos) => {
    const field = armedFieldRef.current;
    if (!field) return;
    const n = normalizeRange(start, end);
    setMappings((prev) => ({
      ...prev,
      [field]: {
        r1: n.r1,
        c1: n.c1,
        r2: n.r2,
        c2: n.c2,
        address: rangeAddress(n.r1, n.c1, n.r2, n.c2),
      },
    }));
    setOverrides((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  React.useEffect(() => {
    const onUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      commitRange(drag.start, drag.current);
      dragRef.current = null;
      setDragPreview(null);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [commitRange]);

  const onCellMouseDown = React.useCallback(
    (row: number, col: number, e: React.MouseEvent) => {
      e.preventDefault();
      if (!armedFieldRef.current) {
        const hit = Object.entries(mappings).find(([, m]) => (
          row >= m.r1 && row <= m.r2 && col >= m.c1 && col <= m.c2
        ));
        if (hit) setArmedField(hit[0]);
        return;
      }
      const pos = { row, col };
      if (e.shiftKey) {
        const mapped = mappings[armedFieldRef.current];
        const anchor = anchorRef.current || (mapped ? { row: mapped.r1, col: mapped.c1 } : pos);
        anchorRef.current = anchor;
        dragRef.current = null;
        setDragPreview(null);
        commitRange(anchor, pos);
        return;
      }
      anchorRef.current = pos;
      dragRef.current = { start: pos, current: pos };
      setDragPreview({ start: pos, current: pos });
    },
    [commitRange, mappings]
  );

  const onCellMouseEnter = React.useCallback((row: number, col: number) => {
    if (!dragRef.current) return;
    const next = { start: dragRef.current.start, current: { row, col } };
    dragRef.current = next;
    setDragPreview(next);
  }, []);

  const cellFieldMap = React.useMemo(() => {
    const m = new Map<string, string[]>();
    Object.entries(mappings).forEach(([field, loc]) => {
      for (let r = loc.r1; r <= loc.r2; r += 1) {
        for (let c = loc.c1; c <= loc.c2; c += 1) {
          const key = `${r}:${c}`;
          const arr = m.get(key) || [];
          arr.push(field);
          m.set(key, arr);
        }
      }
    });
    return m;
  }, [mappings]);

  const dragNorm = dragPreview ? normalizeRange(dragPreview.start, dragPreview.current) : null;

  const fieldSources = React.useMemo(() => {
    const next: Record<string, { kind: 'range' | 'scalar'; values: string[]; address?: string }> = {};
    columns.forEach((c) => {
      if (c.column_name in overrides) {
        next[c.column_name] = { kind: 'scalar', values: [overrides[c.column_name]] };
        return;
      }
      const mapped = mappings[c.column_name];
      if (!mapped) return;
      const values = extractRangeValues(grid, mapped);
      next[c.column_name] = {
        kind: values.length > 1 ? 'range' : 'scalar',
        values,
        address: mapped.address,
      };
    });
    return next;
  }, [columns, grid, mappings, overrides]);

  const rowCount = React.useMemo(() => {
    const lengths = Object.values(fieldSources)
      .filter((s) => s.kind === 'range')
      .map((s) => s.values.length);
    if (lengths.length) return Math.max(...lengths);
    return Object.keys(fieldSources).length ? 1 : 0;
  }, [fieldSources]);

  const lengthMismatch = React.useMemo(() => {
    const lengths = Object.entries(fieldSources)
      .filter(([, s]) => s.kind === 'range')
      .map(([name, s]) => ({ name, n: s.values.length }));
    if (lengths.length < 2) return [];
    const max = Math.max(...lengths.map((x) => x.n));
    return lengths.filter((x) => x.n !== max);
  }, [fieldSources]);

  const { payloadRows, numericCoercionErrors } = React.useMemo(() => {
    const errors: Array<{ row: number; column: string; value: string; dataType: string }> = [];
    if (!rowCount) return { payloadRows: [] as Array<Record<string, any>>, numericCoercionErrors: errors };

    const rows: Array<Record<string, any>> = [];
    for (let i = 0; i < rowCount; i += 1) {
      const row: Record<string, any> = {};
      columns.forEach((c) => {
        const src = fieldSources[c.column_name];
        if (!src) return;

        const raw = src.kind === 'scalar' ? src.values[0] : (src.values[i] ?? '');
        const rawStr = String(raw ?? '');

        if (rawStr !== '' && isNumericType(c.data_type)) {
          const n = parseNumber(rawStr);
          if (n === null) {
            errors.push({ row: i + 1, column: c.column_name, value: rawStr, dataType: c.data_type });
            return;
          }
          row[c.column_name] = n;
          return;
        }

        const coerced = coerceValue(rawStr, c.data_type);
        if (!hasValue(coerced)) return;
        row[c.column_name] = coerced;
      });
      if (Object.keys(row).length) rows.push(row);
    }
    return { payloadRows: rows, numericCoercionErrors: errors };
  }, [columns, fieldSources, rowCount]);

  const missingRequired = React.useMemo(() => {
    if (!payloadRows.length) {
      return columns.filter((c) => c.required && !(c.column_name in fieldSources)).map((c) => c.column_name);
    }
    const missing = new Set<string>();
    payloadRows.forEach((row) => {
      columns.forEach((c) => {
        if (c.required && !hasValue(row[c.column_name])) missing.add(c.column_name);
      });
    });
    return Array.from(missing);
  }, [columns, fieldSources, payloadRows]);

  const clearField = React.useCallback((field: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMappings((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setOverrides((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (armedField === field) setArmedField(null);
  }, [armedField]);

  const onInsert = async () => {
    const table = String(selectedTable || '').trim();
    if (!table) {
      message.error('Select a target table');
      return;
    }
    if (!TABLE_NAME_RE.test(table)) {
      message.error('Invalid table name');
      return;
    }
    if (!payloadRows.length) {
      message.warning('Map at least one field before insert');
      return;
    }
    if (numericCoercionErrors.length) {
      const first = numericCoercionErrors[0];
      message.error(`Invalid numeric value at row ${first.row}, column ${first.column}: "${first.value}" (type ${first.dataType})`);
      return;
    }
    if (missingRequired.length) {
      message.error(`Missing required fields: ${missingRequired.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const body = payloadRows.length === 1 ? payloadRows[0] : payloadRows;
      const result = await insertData(table, body);
      message.success(`Inserted ${payloadRows.length} row${payloadRows.length === 1 ? '' : 's'} into ${table}`);
      onSuccess?.(result);
    } catch (e: any) {
      const backendMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Insert failed';
      message.error(String(backendMsg));
    } finally {
      setSubmitting(false);
    }
  };

  const visibleCols = grid[0]?.length || 0;
  const previewRows = payloadRows.slice(0, PREVIEW_ROW_LIMIT);

  return (
    <div className="xlsx-mapper">
      <div className="xlsx-mapper-toolbar">
        <div>
          <div style={{ marginBottom: 4 }}><Text type="secondary">Target table</Text></div>
          {isTableLocked ? (
            <Input value={selectedTable} disabled style={{ minWidth: 280 }} />
          ) : (
            <Select
              showSearch
              allowClear
              loading={loadingTables}
              placeholder="Select a table"
              options={tableOptions}
              value={selectedTable || undefined}
              onChange={(v) => setSelectedTable(v || '')}
              optionFilterProp="label"
              style={{ minWidth: 320 }}
            />
          )}
        </div>
        <div>
          <div style={{ marginBottom: 4 }}><Text type="secondary">Sheet</Text></div>
          <Select
            disabled={!sheetNames.length}
            options={sheetNames.map((n) => ({ label: n, value: n }))}
            value={sheetName}
            onChange={setSheetName}
            style={{ minWidth: 180 }}
            placeholder="Upload a file first"
          />
        </div>
        {fileName ? <Tag>{fileName}</Tag> : null}
        {rowCount > 0 ? <Tag color="blue">{rowCount} row{rowCount === 1 ? '' : 's'}</Tag> : null}
        {fullGrid.length > MAX_VISIBLE_ROWS ? (
          <Space size={6}>
            <Text type="secondary">Show all rows</Text>
            <Switch size="small" checked={showAllRows} onChange={setShowAllRows} />
          </Space>
        ) : null}
      </div>

      <Dragger
        accept=".csv,.xls,.xlsx"
        multiple={false}
        showUploadList={false}
        beforeUpload={(file) => {
          void loadWorkbook(file);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drop an Excel/CSV file here</p>
        <p className="ant-upload-hint">The file stays in the browser. Nothing is written until you click Insert.</p>
      </Dragger>

      {armedField ? (
        <div className="xlsx-mapper-armed-hint">
          Click and drag cells to map <Text strong>{armedField}</Text>
          {' '}— or click the first cell, then Shift+click the last cell. No Ctrl key.
          <Button type="link" size="small" onClick={() => setArmedField(null)}>Cancel</Button>
        </div>
      ) : (
        <Text type="secondary">
          Select a column on the right, then click-and-drag on the sheet — or click the first cell and Shift+click the last.
        </Text>
      )}

      {truncated ? (
        <Alert
          type="info"
          showIcon
          message={`Showing first ${MAX_VISIBLE_ROWS} rows and ${MAX_VISIBLE_COLS} columns`}
        />
      ) : null}

      {lengthMismatch.length ? (
        <Alert
          type="warning"
          showIcon
          message="Mapped ranges have different lengths. Shorter fields are empty on extra rows."
          description={lengthMismatch.map((x) => `${x.name}: ${x.n}`).join(' · ')}
        />
      ) : null}

      {numericCoercionErrors.length ? (
        <Alert
          type="error"
          showIcon
          message="Some mapped values are not valid numbers for numeric columns."
          description={
            <div>
              <div>Fix the Excel range mapping (or the source values) for these fields:</div>
              <div style={{ marginTop: 6, fontFamily: 'monospace' }}>
                {numericCoercionErrors
                  .slice(0, 8)
                  .map((e) => `row ${e.row} · ${e.column} (${e.dataType}) = "${e.value}"`)
                  .join(' | ')}
                {numericCoercionErrors.length > 8 ? ` | … (+${numericCoercionErrors.length - 8} more)` : ''}
              </div>
            </div>
          }
        />
      ) : null}

      <div className="xlsx-mapper-body">
        <div className="xlsx-mapper-grid-wrap">
          {grid.length ? (
            <table className="xlsx-mapper-grid">
              <thead>
                <tr>
                  <th className="corner" />
                  {Array.from({ length: visibleCols }).map((_, c) => (
                    <th key={c}>{colToLetter(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.map((row, r) => (
                  <tr key={r}>
                    <td className="row-head">{r + 1}</td>
                    {Array.from({ length: visibleCols }).map((_, c) => {
                      const key = `${r}:${c}`;
                      const mappedFields = cellFieldMap.get(key);
                      const isMapped = Boolean(mappedFields?.length);
                      const isActive = Boolean(armedField && mappedFields?.includes(armedField));
                      const isSelecting = Boolean(
                        dragNorm && r >= dragNorm.r1 && r <= dragNorm.r2 && c >= dragNorm.c1 && c <= dragNorm.c2
                      );
                      const cls = [
                        'cell',
                        isMapped ? 'mapped' : '',
                        isActive ? 'mapped-active' : '',
                        isSelecting ? 'selecting' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <td
                          key={c}
                          className={cls}
                          title={mappedFields?.length ? `${cellAddress(r, c)} → ${mappedFields.join(', ')}` : cellAddress(r, c)}
                          onMouseDown={(e) => onCellMouseDown(r, c, e)}
                          onMouseEnter={() => onCellMouseEnter(r, c)}
                        >
                          {row[c] ?? ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 24, color: '#8c8c8c' }}>Upload a workbook to preview cells here.</div>
          )}
        </div>

        <div className="xlsx-mapper-fields">
          <Card size="small" title={selectedTable ? `Columns (${columns.length})` : 'Columns'} loading={loadingColumns}>
            {!selectedTable ? (
              <Text type="secondary">Select a table to load columns.</Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {columns.map((col) => {
                  const mapped = mappings[col.column_name];
                  const src = fieldSources[col.column_name];
                  const isArmed = armedField === col.column_name;
                  const isRange = src?.kind === 'range';
                  const scalarValue = src?.kind === 'scalar' ? (src.values[0] ?? '') : '';
                  const usedFirstColOnly = Boolean(mapped && mapped.c2 > mapped.c1 && mapped.r2 > mapped.r1);
                  return (
                    <div
                      key={col.column_name}
                      className={[
                        'xlsx-mapper-field',
                        isArmed ? 'armed' : '',
                        mapped ? 'mapped' : '',
                        col.foreignKeys.length ? 'fk' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => setArmedField(col.column_name)}
                    >
                      <div className="xlsx-mapper-field-head">
                        <Space size={6} wrap>
                          <Text strong>{col.column_name}</Text>
                          {col.required ? <Tag color="red">required</Tag> : null}
                          <Tag>{col.data_type}</Tag>
                          {col.foreignKeys.map((fk) => (
                            <Tooltip
                              key={`${fk.table}.${fk.column}`}
                              title={`Foreign key → ${fk.table}.${fk.column}`}
                            >
                              <Tag color="purple" icon={<LinkOutlined />}>
                                {fk.table}.{fk.column}
                              </Tag>
                            </Tooltip>
                          ))}
                          {mapped ? <Tag color="blue">{mapped.address}</Tag> : null}
                          {isRange ? <Tag>{src.values.length} values</Tag> : null}
                        </Space>
                        {mapped || col.column_name in overrides ? (
                          <Button
                            size="small"
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={(e) => clearField(col.column_name, e)}
                          />
                        ) : null}
                      </div>
                      {isRange ? (
                        <Text type="secondary" ellipsis>
                          {usedFirstColOnly ? `Using first column ${colToLetter(mapped!.c1)}. ` : ''}
                          {src.values.slice(0, 6).join(', ')}{src.values.length > 6 ? '…' : ''}
                        </Text>
                      ) : (
                        <Input
                          size="small"
                          value={scalarValue}
                          placeholder={isArmed ? 'Click or drag cells…' : 'Arm, then drag a range — or type a constant'}
                          onChange={(e) => {
                            setOverrides((prev) => ({ ...prev, [col.column_name]: e.target.value }));
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>

          <Card
            size="small"
            title={`Payload preview${payloadRows.length ? ` (${payloadRows.length})` : ''}`}
            className="xlsx-mapper-payload"
          >
            {missingRequired.length ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message={`Missing required: ${missingRequired.join(', ')}`}
              />
            ) : null}
            <pre>
              {JSON.stringify(
                payloadRows.length <= PREVIEW_ROW_LIMIT
                  ? (payloadRows.length === 1 ? payloadRows[0] : payloadRows)
                  : { showing: previewRows, total_rows: payloadRows.length },
                null,
                2
              )}
            </pre>
            <Space style={{ marginTop: 8, width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setMappings({});
                  setOverrides({});
                  setArmedField(null);
                }}
                disabled={submitting}
              >
                Clear mappings
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={submitting}
                disabled={!selectedTable || !payloadRows.length || numericCoercionErrors.length > 0 || submitting}
                onClick={onInsert}
              >
                Insert{payloadRows.length > 1 ? ` ${payloadRows.length} rows` : ''}
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default XlsxSectionMapper;
